/* eslint-disable max-lines */
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { css } from 'styled-components'
import moment from 'moment'
import _ from 'underscore'
import EditIcon from '@mui/icons-material/Edit'
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined'
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined'

import CollapsibleMessage from 'pages/SiteMessages/FeatureTours/CollapsibleMessage'

import AddButton from 'components/AddButton'
import Checkbox from 'components/Checkbox'
import Table from 'components/Table'

import Api from 'services/api'

import { shortDate } from 'helpers/formatters'
import Brand from 'helpers/brand'

import useFeatureTours from 'hooks/useFeatureTours'

import { color } from 'styles/colors'
import { screenSize, LoadingSinglePanePage } from 'styles/shared'

const FeatureTours = () => {
  const navigate = useNavigate()
  const brands = Brand.allBrandsOfCurrentBusinessUnit()
  const [refreshTimestamp, setRefreshTimestamp] = useState(moment().valueOf())
  const { featureTours, areFeatureToursLoaded } = useFeatureTours(brands, false, refreshTimestamp)
  const [showArchived, setShowArchived] = useState(false)

  if (!areFeatureToursLoaded) {
    return <LoadingSinglePanePage />
  }

  const onClickArchive = (featureTourId) => {
    Api.FeatureTours.archive(featureTourId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  const onClickRestore = (featureTourId) => {
    Api.FeatureTours.restore(featureTourId).then(() => { setRefreshTimestamp(Date.now()) })
  }

  const sortedFeatureTours = () => (
    _.chain(featureTours)
      .filter((featureTour) => !featureTour.archivedAt || showArchived)
      .sortBy(featureTour => {
        let sortValue = moment(featureTour.publishedAt || featureTour.updatedAt).toDate().getTime()

        if (featureTour.publishedAt) { sortValue = sortValue * 10 }
        if (!featureTour.archivedAt) { sortValue = sortValue * 100 }

        return sortValue
      })
      .reverse()
      .value()
  )

  const renderItemAction = (featureTour) => {
    if (!(activeFeatureTours().includes(featureTour.id))) {
      if (!featureTour.archivedAt) {
        return <S.ArchiveOutlinedIcon role="img" onClick={() => onClickArchive(featureTour.id)} aria-label="Archive Feature Tour" />
      } else {
        return <S.RestoreOutlinedIcon role="img" onClick={() => onClickRestore(featureTour.id)} aria-label="Restore Feature Tour" />
      }
    }

    return null
  }

  const renderStatus = (featureTour) => {
    const publishedStatus = { status: '', date: '' }
    const active = activeFeatureTours().includes(featureTour.id)
    if (active && featureTour.published) {
      publishedStatus.status = 'published'
      publishedStatus.date = featureTour.publishedAt
    } else if (featureTour.published && featureTour.expired) {
      publishedStatus.status = 'expired'
      publishedStatus.date = featureTour.expiredAt
    } else if (featureTour.published && !featureTour.expired) {
      publishedStatus.status = 'previously published'
      publishedStatus.date = featureTour.publishedAt
    } else {
      publishedStatus.status = 'not published'
    }
    return (
      <>
        <S.Status status={publishedStatus.status}> {publishedStatus.status}</S.Status>
        {publishedStatus.date && <span style={{ margin: 8 }}>{shortDate(publishedStatus.date)}</span>}
      </>)
  }

  const activeFeatureTours = () => {
    const latestActive : string[] = []

    brands.forEach((brand) => {
      const publishedlist = sortedFeatureTours().find((featureTour) => featureTour.publishedAt && featureTour.brands.includes(brand))

      if (publishedlist && !latestActive.includes(publishedlist.id) && !publishedlist.expiredAt) {
        latestActive.push(publishedlist.id)
      }
    })

    return latestActive
  }

  const MESSAGE_COLUMN_FLEX = 1.5

  return (
    <>
      <S.Header>
        <S.Checkbox
          label="Show archived feature tours"
          checked={showArchived}
          onChange={() => setShowArchived(!showArchived)}
        />
        <AddButton uri="/admin/feature_tours/new">Add Feature Tour</AddButton>
      </S.Header>
      <Table
        items={featureTours}
        noDataMessage={{
          title: 'No Feature Tours',
          description: 'Click "Add Feature Tour" to add your first feature tour.'
        }}
      >
        <S.TableHeader>
          <S.HeaderCell title="Internal Name" />
          <S.HeaderCell title="Title" />
          <S.MessageHeaderCell title="Message" flexSpacing={MESSAGE_COLUMN_FLEX} />
          <S.HeaderCell title="Role / Brand" flexSpacing={MESSAGE_COLUMN_FLEX} />
          <S.HeaderCell title="Tiers" />
          <S.HeaderCell title="Status / Date" />
        </S.TableHeader>

        {sortedFeatureTours().map((featureTour) => (
          <S.Row key={featureTour.id}>
            <S.NameCell>
              <S.Name>{featureTour.internalName}</S.Name>
            </S.NameCell>
            <S.TittleCell>
              <S.Title>{featureTour.title}</S.Title>
            </S.TittleCell>
            <S.MessageCell flexSpacing={MESSAGE_COLUMN_FLEX}>
              <CollapsibleMessage text={featureTour.message} />
            </S.MessageCell>
            <S.StatusCell flexSpacing={MESSAGE_COLUMN_FLEX}>
              <S.RolesAndBrands>
                {featureTour.roles.map((role) => (
                  <S.Role key={role} isFsr={role === 'fsr'}>{role}</S.Role>
                ))}
                {
                  featureTour.brands.map(brand => {
                    const productName = Brand.productName(brand)
                    const logoSrc = Brand.brandLogoWithOutText(brand)
                    return (
                      <S.ProductLogo
                      key={productName}
                      src={logoSrc}
                      aria-label="product-logo"
                      alt={`${productName} logo`}
                      />
                    )
                  })
                }
              </S.RolesAndBrands>
            </S.StatusCell>
            <S.StatusCell>
              <S.Tiers>{featureTour?.tiers?.join(', ')}</S.Tiers>
            </S.StatusCell>
            <S.StatusCell>
              <S.Actions>
                <S.PublishedContainer>
                  {renderStatus(featureTour)}
                </S.PublishedContainer>
                <S.ActionIcons>
                  <S.EditIcon
                    fontSize="small"
                    color="primary"
                    style={{ cursor: 'pointer' }}
                    aria-label={`Edit Feature Tour with title: ${featureTour.title}`}
                    onClick={() => navigate(`/admin/feature_tours/${featureTour.id}/edit`)}
                  >
                    Edit
                  </S.EditIcon>
                  {renderItemAction(featureTour)}

                </S.ActionIcons>
              </S.Actions>
            </S.StatusCell>
          </S.Row>
        ))}
      </Table>
    </>
  )
}

export default FeatureTours

const iconStyle = (iconType) => {
  return css`
    cursor: pointer;
    color: ${color(iconType)};
    &:hover {
      color: ${color(`${iconType}Hover`)};
    }
  `
}

const MESSAGE_COLUMN_FLEX_VALUE = '1'
const STATUS_COLUMN_FLEX_VALUE = '1'

const S = {
  Header: styled.div`
    display: flex;
    gap: 30px;
    justify-content: end;
    position: relative;
    top: -3px;
    margin: 15px 20px 5px 0;
  `,
  Checkbox: styled(Checkbox)`
    text-transform: capitalize;
    font-weight: 600;
    color: ${color('pageTitle')};
  `,
  MessageHeaderCell: styled(Table.HeaderCell).withConfig({
    shouldForwardProp: (prop) => !['flexSpacing'].includes(prop)
  })<{ flexSpacing?: number | null }>`
    background-color: ${color('customerPinUnpinned')};  
    flex: ${({ flexSpacing }) => flexSpacing ?? MESSAGE_COLUMN_FLEX_VALUE};
  `,
  HeaderCell: styled(Table.HeaderCell).withConfig({
    shouldForwardProp: (prop) => !['flexSpacing'].includes(prop)
  })<{ flexSpacing?: number | null }>`
    background-color: ${color('customerPinUnpinned')};
    flex: ${({ flexSpacing }) => flexSpacing ?? STATUS_COLUMN_FLEX_VALUE};
    border-bottom-left-radius: 0 !important;
    
    &:last-child {
      border-bottom-right-radius: 0 !important;
    } 
    `,
  Row: styled(Table.Row)`
    padding: 10px 0;
    align-items: center;
    background-color: ${color('infoPanelHeaderBackground')};
    border-bottom: 1px solid ${color('customerPinUnpinned')};
    margin: 0 5px;
    &:last-child {
      border-bottom: none;
    }

    @media ${screenSize.small} {
      flex-flow: row wrap;
      padding: 10px 0 10px;
    }
  `,
  MessageCell: styled(Table.Cell).withConfig({
    shouldForwardProp: (prop) => !['flexSpacing'].includes(prop)
  })<{ flexSpacing?: number | null }>`
    flex: ${({ flexSpacing }) => flexSpacing ?? MESSAGE_COLUMN_FLEX_VALUE};
    flex-direction: column;
    padding: 0px 15px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media ${screenSize.small} {
      flex: 1 1 100%;
      margin-right: 0;
      margin-bottom: 15px;
    }
  `,
  Title: styled.div`
    color: ${color('primaryText')};
    font-weight: 700;
    flex: 1;
    text-transform: capitalize;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  Name: styled.div`
    color: ${color('primaryText')};
    font-weight: 700;
    flex: 1;
    text-transform: capitalize;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  RolesAndBrands: styled.div`
    flex: 1;
    display: flex;
    gap: 8px;
    align-items: flex-start;
  `,
  Actions: styled.div`
   display: flex;
   gap: 8px;
   width: 100%;
   justify-content: space-between;
 `,
  Role: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isFsr'
  })<{ isFsr: boolean }>`
    font-style: italic;
    background-color: ${color('DashboardThermostatText')};
    color: ${color('newLabelText')};
    text-transform: ${({ isFsr }) => isFsr ? 'uppercase' : 'capitalize'};
    color: ${color('newLabelText')};
    border-radius: 4px;
    padding: 4px 8px;
  `,
  Tiers: styled.div`
    flex: 1;
    font-weight: 400;
    text-transform: capitalize;
    @media ${screenSize.small} {
      padding: 0px 15px;
      flex: 1 1 100%;
      margin-right: 0;
      margin-bottom: 15px;
    }
  `,
  ProductLogo: styled.img`
    display: flex;
    height: 33px;
    justify-content: center;
  `,
  PublishedContainer: styled.div`
    @media ${screenSize.small} {
      display: inline-block;
      width: 100%;
      margin-bottom: 15px;
      padding: 0px 15px;
    }
  `,
  TittleCell: styled(Table.Cell)`
    flex: ${STATUS_COLUMN_FLEX_VALUE};
    flex-direction: row;
    padding:  0px 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;

    @media ${screenSize.small} {
      justify-content: center;
      align-items: center;
      margin-bottom: 15px;
    }
  `,
  StatusCell: styled(Table.Cell).withConfig({
    shouldForwardProp: (prop) => !['flexSpacing'].includes(prop)
  })<{ flexSpacing?: number | null }>`
    flex: ${({ flexSpacing }) => flexSpacing ?? STATUS_COLUMN_FLEX_VALUE};
    flex-direction: column;
    padding: 0px 15px;
    align-items: flex-start;
    justify-content: center;

    @media ${screenSize.small} {
      padding: 0px 15px;
      display: contents;
      width: 100%;
      margin-bottom: 15px;
    }
  `,
  NameCell: styled.div`
    flex: ${STATUS_COLUMN_FLEX_VALUE};
    display: flex;
    justify-content: space-between;
    align-items: center;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0px 15px;

    @media ${screenSize.small} {
      flex: 1 1 100%;
      margin-right: 0px;
      padding: 0px 15px;
      margin-bottom: 15px;
    }
  `,
  Status: styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'status'
  })<{ status: string }>`
    display: inline-block;
    text-transform: capitalize;
    ${({ status }) =>
    status === 'published' &&
      `
      
        color: ${color('positiveStatus')};
      `}
  `,
  ArchiveOutlinedIcon: styled(ArchiveOutlinedIcon)`
  ${iconStyle('actionableIcon')}
`,
  RestoreOutlinedIcon: styled(RestoreOutlinedIcon)`
    ${iconStyle('actionableIcon')}
  `,
  EditIcon: styled(EditIcon)`
    color: ${color('EditIcon')};
  `,
  ActionIcons: styled.div`
    display: flex;
    width: 50px;
    gap: 10px;
    justify-content: left;
  `,
  TableHeader: styled(Table.Header)`
    padding: 0px 5px;
  `
}
